/**
 * Receipt-scan flags for one app session. Not persisted on purpose: a fresh
 * session asks the server again, so a fair-use cap or a `ProNotAvailable`
 * that has since been lifted never sticks.
 */
import { create } from 'zustand';

import { currentUtcMonth } from '../scan/scanState';

export interface ScanSessionFlags {
  /** The API answered `ProNotAvailable`: the receipt block hides. */
  proNotAvailableSeen: boolean;
  /** The server's fair-use refusal: scanning reads as exhausted, Pro too. */
  fairUseHit: boolean;
}

export interface ScanSessionStore extends ScanSessionFlags {
  /** Whose session the flags describe, and the UTC month they were set in. */
  userId: string | null;
  monthKey: string;
  markProNotAvailable: () => void;
  markFairUseHit: () => void;
  reset: () => void;
  /**
   * Clears the flags when the signed-in user or the UTC month changed. A
   * phone keeps a backgrounded process for days, so a fair-use hit on the
   * last day of a month must not outlive it.
   */
  bindToUser: (userId: string | null | undefined, monthKey?: string) => void;
}

const clearedFlags: ScanSessionFlags = {
  proNotAvailableSeen: false,
  fairUseHit: false,
};

/** Call once per app process; both apps hold one instance. */
export function createScanSessionStore(deps: { now?: () => Date } = {}) {
  const month = () => currentUtcMonth(deps.now?.() ?? new Date());
  return create<ScanSessionStore>()((set, get) => {
    // A flag left from an earlier month must not come back to life under
    // the new month's stamp.
    const mark = (flag: Partial<ScanSessionFlags>) => {
      const monthKey = month();
      const carried = get().monthKey === monthKey ? {} : { ...clearedFlags };
      set({ ...carried, ...flag, monthKey });
    };
    return {
      ...clearedFlags,
      userId: null,
      monthKey: month(),
      markProNotAvailable: () => mark({ proNotAvailableSeen: true }),
      markFairUseHit: () => mark({ fairUseHit: true }),
      reset: () => set({ ...clearedFlags, monthKey: month() }),
      bindToUser: (userId, monthKey = month()) => {
        const next = userId ?? null;
        const state = get();
        if (state.userId === next && state.monthKey === monthKey) return;
        set({ ...clearedFlags, userId: next, monthKey });
      },
    };
  });
}

/** The flags as they apply in `nowMonth`: a past month's reads as cleared, even before a bind. */
export function scanSessionFlagsFor(
  state: Pick<
    ScanSessionStore,
    'proNotAvailableSeen' | 'fairUseHit' | 'monthKey'
  >,
  nowMonth: string
): ScanSessionFlags {
  if (state.monthKey !== nowMonth) return clearedFlags;
  return {
    proNotAvailableSeen: state.proNotAvailableSeen,
    fairUseHit: state.fairUseHit,
  };
}

export type ScanSessionStoreHook = ReturnType<typeof createScanSessionStore>;

/**
 * Keeps `scanSession` bound to the auth store's user: every sign-out path and
 * any account switch goes through it. Returns the unsubscribe.
 */
export function bindScanSessionToAuth(
  scanSession: ScanSessionStoreHook,
  authStore: {
    getState: () => { user: { id: string } | null };
    subscribe: (
      listener: (state: { user: { id: string } | null }) => void
    ) => () => void;
  }
): () => void {
  scanSession.getState().bindToUser(authStore.getState().user?.id);
  return authStore.subscribe(state =>
    scanSession.getState().bindToUser(state.user?.id)
  );
}
